import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { ShieldCheck, Lock, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import { getResellerInviteInfo } from "../../utils/users";
import { SERVER_URL } from "../../utils/axios";
import QuickBooksConnectButton from "../../components/QuickBooksConnectButton";

const ResellerInvite = () => {
  const { referralCode } = useParams();
  const [reseller, setReseller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (referralCode) {
      getResellerInviteInfo(referralCode)
        .then((res) => {
          if (res.data.success && res.data.reseller) {
            setReseller(res.data.reseller);
          } else {
            setError(res.data.message || "Invalid or expired invitation link.");
          }
        })
        .catch((err) => {
          console.error(err);
          setError("Unable to load invitation details. Please check your link or contact support.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setError("Missing invitation code.");
      setLoading(false);
    }
  }, [referralCode]);

  const handleConnectXero = () => {
    window.location.href = `${SERVER_URL}/callback/reseller_invite_xero?code=${encodeURIComponent(referralCode)}`;
  };

  const handleConnectQbo = () => {
    window.location.href = `${SERVER_URL}/callback/reseller_invite_qbo?code=${encodeURIComponent(referralCode)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 via-white to-gray-50 flex flex-col justify-between py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto w-full">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-500/30 mb-4">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            WOW Backup & Restore
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Automated Cloud Backup & Disaster Recovery Protection
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="py-20 text-center flex flex-col items-center justify-center gap-4">
              <Spin size="large" />
              <p className="text-sm text-gray-500 font-medium">Verifying invitation link...</p>
            </div>
          ) : error ? (
            <div className="p-8 sm:p-10 text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 mx-auto flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Invitation Error</h3>
              <p className="text-sm text-gray-600 mb-6">{error}</p>
              <p className="text-xs text-gray-400">
                Please contact the partner who provided this link or reach out to support@wowbackupandrestore.com.
              </p>
            </div>
          ) : (
            <div className="p-6 sm:p-10">
              {/* Partner Banner */}
              <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">
                    Partner Invitation
                  </p>
                  <p className="text-sm text-blue-950 mt-0.5">
                    <span className="font-semibold">{reseller?.full_name}</span> has invited you to connect your organisation to their management portal.
                  </p>
                </div>
              </div>

              {/* Explanatory text */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Connect your accounting software
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Authorize read-only backup access for your organisation. Once connected, <strong>{reseller?.full_name}</strong> will oversee automated continuous backups, tamper-proof archives, and instant disaster recovery on your behalf.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3.5">
                <button
                  type="button"
                  onClick={handleConnectXero}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl border border-sky-300 bg-sky-500 hover:bg-sky-600 text-white font-medium shadow-md shadow-sky-500/20 transition-all hover:shadow-lg cursor-pointer"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.125 17.5h-2.25l2.625-5.5-2.625-5.5h2.25l2.625 5.5-2.625 5.5zm4.5 0h-2.25l2.625-5.5-2.625-5.5h2.25l2.625 5.5-2.625 5.5z"/>
                  </svg>
                  <span>Connect with Xero</span>
                </button>

                <div className="flex justify-center pt-1">
                  <QuickBooksConnectButton onClick={handleConnectQbo} />
                </div>
              </div>

              {/* Trust & Security Notes */}
              <div className="mt-8 pt-6 border-t border-gray-100 grid grid-cols-2 gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>256-bit AES encryption</span>
                </div>
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>Daily automated backup</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-xs text-gray-400">
        &copy; {new Date().getFullYear()} WOW Backup &amp; Restore. All rights reserved.
      </div>
    </div>
  );
};

export default ResellerInvite;
